import { useEffect, useRef } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";

import Helpers from "../../commons/helpers";

import { IPageData } from "./useComment";
import { IDataComment } from "../../commons/interfaces";
import { showAlert } from "../../providers/AlertProvider";
import { useCommentContext } from "../../providers/CommentProvider";
import { DiscussionGroupType, VoteType } from "../../constants/enums";
import { Constants, MESSAGE_HUB_URL } from "../../constants/constants";



interface IProps {
    discussionGroupType: DiscussionGroupType;
    setListComments: React.Dispatch<React.SetStateAction<IDataComment[]>>;
    setPageData: React.Dispatch<React.SetStateAction<IPageData>>;
    listCommentRef: React.MutableRefObject<any[]>;
}

const useSignalr = ({ listCommentRef, discussionGroupType, setListComments, setPageData }: IProps) => {
    const connectionRef = useRef<any>();
    const { referenceId, userLogin, clientId } = useCommentContext();

    useEffect(() => {
        joinRoom(userLogin).then(() => {
            getConnectionMessage();
        });
    }, []);

    const getConnectionMessage = async () => {
        try {
            if (connectionRef.current) {
                connectionRef.current.invoke(Constants.ChatHubName.GET_CONNECTION).then((data: any) => { });
            }
        } catch (error) {
            showAlert(Helpers.renderExceptionError(error), "error");
        }
    };

    const joinRoom = async (userLogin?: any) => {
        try {
            var data = {
                UserId: userLogin?.userId,
                ReferenceId: referenceId,
                Type: discussionGroupType,
                UserName: userLogin?.userName,
                clientId: clientId,
            };

            const connection = new HubConnectionBuilder().withUrl(MESSAGE_HUB_URL).configureLogging(LogLevel.Information).build();

            connection.on(Constants.ChatHubName.RECEIVE_CREATE_MESSAGE, async (user: any, message: any) => {
                const jsonMessage = JSON.parse(message);

                let listCommentTemp = [...listCommentRef.current];

                jsonMessage?.value?.forEach((el: any) => {
                    let commentResponse: any = {
                        channelId: el?.channelId,
                        id: el?.id,
                        discussionValue: el?.discussionValue,
                        threadId: el?.threadId,
                        threadGene: el?.threadGene,
                        totalReply: el?.totalReply,
                        userComment: {
                            fullName: el?.fullName,
                            userId: el?.userId,
                            userName: el?.userName,
                            avatar: el?.avatarUrl,
                        },
                        timeComment: el?.createTime,
                        listRepliesToComment: [],
                        rating: el.rating,
                    };
                    if (el?.replyTo) {
                        const index = listCommentTemp.findIndex((e) => e.id === el?.replyTo);
                        listCommentTemp.splice(index + 1, 0, commentResponse);
                    } else {
                        listCommentTemp.unshift(commentResponse);
                        setPageData((prev) => ({ ...prev, totalCount: prev.totalCount + 1 }));
                    }
                });

                let result = Helpers.unique(listCommentTemp);
                listCommentRef.current = result;
                setListComments(result);
            });

            connection.on(Constants.ChatHubName.JOIN_ROOM_RESPONSE, async (message: any) => {
                const jsonMessage = JSON.parse(message);
                !jsonMessage.isSuccess && showAlert("Có lỗi xảy ra. Vui lòng thử lại!", "error");
            });

            connection.on(Constants.ChatHubName.RESPONSE_CREATE_MESSAGE, async (message: any) => {
                const jsonMessage = JSON.parse(message);
                !jsonMessage.isSuccess && showAlert(message.error, "error");
            });

            connection.on(Constants.ChatHubName.RESPONSE_DELETE_MESSAGE, async (message: any) => {
                const jsonMessage = JSON.parse(message);
                jsonMessage.isSuccess && showAlert("Xóa thành công", "success");
            });

            connection.on(Constants.ChatHubName.RESPONSE_UPDATE_MESSAGE, async (message: any) => {
                const jsonMessage = JSON.parse(message);
                jsonMessage.isSuccess && showAlert("Cập nhật thành công", "success");
            });

            connection.on(Constants.ChatHubName.RESPONSE_UPVOTE_MESSAGE, async (message: any) => {
                const jsonMessage = JSON.parse(message);
                jsonMessage.isSuccess && showAlert("Cập nhật thành công", "success");
            });

            connection.on(Constants.ChatHubName.RESPONSE_DOWNVOTE_MESSAGE, async (message: any) => {
                const jsonMessage = JSON.parse(message);
                jsonMessage.isSuccess && showAlert("Cập nhật thành công", "success");
            });

            connection.on(Constants.ChatHubName.RECEIVE_UPDATE_MESSAGE, async (user: any, message: any) => {
                const jsonMessage = JSON.parse(message);

                let listCommentTemp = [...listCommentRef.current];

                const index = listCommentTemp.findIndex((e) => e.id === jsonMessage?.value?.id);

                listCommentTemp[index].discussionValue = jsonMessage?.value?.discussionValue;
                listCommentRef.current = listCommentTemp;

                setListComments(listCommentTemp || []);
            });

            connection.on(Constants.ChatHubName.RECEIVE_DELETE_MESSAGE, async (user: any, message: any) => {
                const jsonMessage = JSON.parse(message);

                let listCommentTemp = listCommentRef.current?.filter((e) => {
                    if (jsonMessage.value.threadGene.length === e.threadGene.length) return e.id !== jsonMessage.value.id;
                    return !e.threadGene.startsWith(jsonMessage.value.threadGene);
                });

                // totalCount--;
                listCommentRef.current = listCommentTemp;

                setListComments(listCommentTemp);
            });

            connection.on(Constants.ChatHubName.RECEIVE_UPVOTE_MESSAGE, async (user: any, message: any) => {
                const jsonMessage = JSON.parse(message);

                let listCommentTemp = [...listCommentRef.current];

                const index = listCommentTemp.findIndex((e) => e.rating?.id === jsonMessage?.value?.ratingId);

                if (jsonMessage?.value?.userId === userLogin?.userId) {
                    if (listCommentTemp[index].rating.myRating?.type !== VoteType.Up) {
                        listCommentTemp[index].rating.totalUpvote = listCommentTemp[index].rating.totalUpvote + 1;
                    }
                    listCommentTemp[index].rating.myRating = jsonMessage?.value;
                } else {
                    listCommentTemp[index].rating.totalUpvote = listCommentTemp[index].rating.totalUpvote + 1;
                }

                listCommentRef.current = listCommentTemp;

                setListComments(listCommentTemp || []);
            });

            connection.on(Constants.ChatHubName.RECEIVE_DOWNVOTE_MESSAGE, async (user: any, message: any) => {
                const jsonMessage = JSON.parse(message);

                let listCommentTemp = [...listCommentRef.current];

                const index = listCommentTemp.findIndex((e) => e.rating?.id === jsonMessage?.value?.ratingId);

                if (jsonMessage?.value?.userId === userLogin?.userId) {
                    if (listCommentTemp[index].rating.myRating?.type === VoteType.Up) {
                        listCommentTemp[index].rating.totalUpvote = listCommentTemp[index].rating.totalUpvote - 1;
                    }
                    listCommentTemp[index].rating.myRating = jsonMessage?.value;
                } else {
                    listCommentTemp[index].rating.totalUpvote = listCommentTemp[index].rating.totalUpvote - 1;
                }

                listCommentRef.current = listCommentTemp;

                setListComments(listCommentTemp || []);
            });

            await connection.start();
            await connection.invoke(Constants.ChatHubName.JOIN_ROOM, data);

            localStorage.setItem(Constants.StorageKey.USER_LOGIN, JSON.stringify(userLogin));

            connectionRef.current = connection;
        } catch (error) {
            showAlert(Helpers.renderExceptionError(error), "error");
        }
    };

    const deleteMessage = async (messageId: string) => {
        try {
            if (connectionRef.current) {
                let request = { messageId };
                await connectionRef.current.invoke(Constants.ChatHubName.DELETE_MESSAGE, request);
            }
        } catch (error) {
            showAlert(Helpers.renderExceptionError(error), "error");
        }
    };

    const sendMessage = async (message: string, replyTo?: string) => {
        try {
            await getConnectionMessage();
            if (connectionRef.current) {
                let request = { messageText: message, replyTo };
                await connectionRef.current.invoke(Constants.ChatHubName.SEND_MESSAGE, request);
            }
        } catch (error) {
            showAlert(Helpers.renderExceptionError(error), "error");
        }
    };

    const updateMessage = async (messageId: string, message: string) => {
        try {
            if (connectionRef.current) {
                let request = { messageId, message };
                await connectionRef.current.invoke(Constants.ChatHubName.UPDATE_MESSAGE, request);
            }
        } catch (e) {
            showAlert("Có lỗi xảy ra. Vui lòng thử lại!", "error");
        }
    };

    const onUpOrDownVote = async (ratingId: string, isDownVote: boolean) => {
        try {
            if (connectionRef.current) {
                let request = { ratingId };
                if (isDownVote) {
                    await connectionRef.current.invoke(Constants.ChatHubName.DOWN_VOTE, request);
                } else {
                    await connectionRef.current.invoke(Constants.ChatHubName.UP_VOTE, request);
                }
            }
        } catch (error) {
            showAlert(Helpers.renderExceptionError(error), "error");
        }
    };

    return { joinRoom, deleteMessage, sendMessage, updateMessage, onUpOrDownVote };
};
export default useSignalr;
